/**
 * Currency formatting utilities
 */
import i18n from '@/constants/i18n';

import { formatPrice } from './internationalizationUtils';

/**
 * Get the currency symbol for a given ISO 4217 currency code.
 * @param currency - ISO 4217 currency code (e.g., "USD", "GBP"). Defaults to "USD".
 * @returns The currency symbol (e.g., "$", "£", "€")
 */
export const getCurrencySymbol = (currency: string = 'USD'): string => {
  try {
    const symbol = new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
    })
      .formatToParts(0)
      .find((part) => part.type === 'currency')?.value;
    if (symbol) return symbol;
  } catch {
    // narrowSymbol not supported in this environment, fall through to symbol
  }

  try {
    return (
      new Intl.NumberFormat(i18n.language, {
        style: 'currency',
        currency,
        currencyDisplay: 'symbol',
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? '$'
    );
  } catch {
    return '$';
  }
};

/**
 * Format currency amount in cents to a localized string without cents
 * @param amount - Amount in cents
 * @param currency - ISO 4217 currency code (e.g., "USD", "GBP", "CAD"). Defaults to "USD".
 * @returns Formatted currency string (e.g., "$10", "£10")
 */
export const formatCurrency = (
  amount: number,
  currency: string = 'USD',
): string => {
  return formatPrice(amount / 100, { currency, showCents: false });
};

/**
 * Format currency amount in cents to a localized string with cents
 * @param amount - Amount in cents
 * @param currency - ISO 4217 currency code (e.g., "USD", "GBP", "CAD"). Defaults to "USD".
 * @returns Formatted currency string (e.g., "$10.50", "£10.50")
 */
export const formatCurrencyWithCents = (
  amount: number,
  currency: string = 'USD',
): string => {
  return formatPrice(amount / 100, { currency, showCents: true });
};

/**
 * Format currency amount in cents, showing cents only when the amount is not a whole dollar.
 * @param amount - Amount in cents
 * @param currency - ISO 4217 currency code (e.g., "USD", "GBP", "CAD"). Defaults to "USD".
 * @returns Formatted currency string (e.g., "$10" for 1000, "$10.50" for 1050)
 */
export const formatCurrencyOptionalCents = (
  amount: number,
  currency: string = 'USD',
): string => {
  const showCents = amount % 100 !== 0;
  return formatPrice(amount / 100, { currency, showCents });
};
